import { z } from 'zod'

// ── File upload / storage types ──────────────────────────────────────────────

/** One entry of the canonical allowlist served by GET /files/config. */
export const allowedFileTypeSchema = z.object({
  value: z.string(),          // canonical MIME type (stored in validations.allowedFileTypes)
  label: z.string(),
  mimeTypes: z.array(z.string()),
  extensions: z.array(z.string()),
})

/** Global upload policy (GET /files/config). */
export const fileUploadConfigSchema = z.object({
  maxFileSizeBytes: z.number().int().positive(),
  allowedTypes: z.array(allowedFileTypeSchema),
})

/** Response of any upload endpoint (record value / activity / public form). */
export const uploadedFileSchema = z.object({
  key: z.string(),
  name: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  contentType: z.string().nullable().optional(),
  downloadUrl: z.string().nullable().optional(),
  /** Saves the file under its name instead of displaying it (Content-Disposition: attachment). */
  attachmentUrl: z.string().nullable().optional(),
})

/** Entry of POST /files/metadata — metadata plus a pre-signed download URL. */
export const fileMetadataSchema = z.object({
  key: z.string(),
  name: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  contentType: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  uploadedAt: z.string().nullable().optional(),
  downloadUrl: z.string().nullable().optional(),
  /** Saves the file under its name instead of displaying it (Content-Disposition: attachment). */
  attachmentUrl: z.string().nullable().optional(),
})

export type AllowedFileType = z.infer<typeof allowedFileTypeSchema>
export type FileUploadConfig = z.infer<typeof fileUploadConfigSchema>
export type UploadedFile = z.infer<typeof uploadedFileSchema>
export type FileMetadata = z.infer<typeof fileMetadataSchema>

/** Options accepted by every upload helper. */
export interface FileUploadOptions {
  onProgress?: (percent: number) => void
  signal?: AbortSignal
  /** `gzip` when the bytes were compressed in the browser (text-like files); the server stores them as such. */
  contentEncoding?: 'gzip'
  /** Size of the file before compression, in bytes. */
  originalSize?: number
}
